"use client";
import {m} from "framer-motion";
import {useEffect, useState} from "react";
import Link from "next/link";
import {FiGithub, FiLinkedin, FiDownload, FiArrowRight} from "react-icons/fi";
import {HiOutlineMail} from "react-icons/hi";

const words = [
  "web apps",
  "landing pages",
  "design systems",
  "user interfaces",
  "responsive designs",
  "modern websites",
  "dynamic apps",
];

export default function HeroSection() {
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [forward, setForward] = useState(true);

  useEffect(() => {
    if (forward && subIndex < words[index].length) {
      const t = setTimeout(() => setSubIndex(subIndex + 1), 120);
      return () => clearTimeout(t);
    } else if (!forward && subIndex > 0) {
      const t = setTimeout(() => setSubIndex(subIndex - 1), 60);
      return () => clearTimeout(t);
    } else if (forward && subIndex === words[index].length) {
      const t = setTimeout(() => setForward(false), 1000);
      return () => clearTimeout(t);
    } else if (!forward && subIndex === 0) {
      setForward(true);
      setIndex((index + 1) % words.length);
    }
  }, [index, subIndex, forward]);

  return (
    <section className="relative flex items-center justify-center min-h-screen overflow-hidden px-6 font-mono">
      {/* Blueprint grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(250, 204, 21, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(250, 204, 21, 0.15) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto text-center">
        <m.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, ease: "easeOut"}}
        >
          {/* Badge */}
          <m.div
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            transition={{delay: 0.2, duration: 0.5}}
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-900 border-2 border-yellow-400 text-yellow-400 text-xs font-bold uppercase mb-8 rounded-none"
          >
            <span className="w-2 h-2 bg-yellow-400 animate-pulse rounded-none" />
            SYSTEM_STATUS: ONLINE_FOR_HIRE
          </m.div>

          <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-black uppercase leading-[1.1] tracking-tighter text-zinc-50">
            Hi, I&apos;m <span className="text-yellow-400">Joel Olajire</span>
          </h1>

          <h2 className="mt-6 text-lg sm:text-xl md:text-2xl text-zinc-400 font-medium lowercase">
            I_BUILD{" "}
            <span className="relative text-zinc-50 uppercase font-black tracking-tight border-b-2 border-yellow-400 pb-0.5">
              {words[index].substring(0, subIndex)}
              <m.span
                animate={{opacity: [1, 0]}}
                transition={{
                  duration: 0.5,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="inline-block w-[3px] h-6 bg-yellow-400 ml-1 align-middle"
              />
            </span>
          </h2>

          <m.p
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.4, duration: 0.6}}
            className="mt-6 text-xs sm:text-sm text-zinc-400 max-w-xl mx-auto leading-relaxed"
          >
            Frontend Engineer specializing in performant, highly accessible, and visually striking terminal interfaces. Deploying production-ready utilities with clean structures.
          </m.p>

          <m.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{delay: 0.6, duration: 0.6}}
            className="mt-10 flex flex-wrap justify-center gap-4"
          >
            <Link
              href="#projects"
              className="group inline-flex items-center gap-2 px-6 py-3 border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-zinc-950 font-bold transition-none rounded-none text-sm hover-glitch"
            >
              ACCESS_LOGS
              <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="/api/cv"
              download
              aria-label="Download Joel Olajire's Resume PDF"
              className="group inline-flex items-center gap-2 px-6 py-3 border-2 border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800 text-zinc-400 hover:text-zinc-50 font-bold transition-none rounded-none text-sm hover-glitch"
            >
              <FiDownload className="group-hover:-translate-y-0.5 transition-transform" />
              DOWNLOAD_CV
            </a>
          </m.div>

          <m.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{delay: 0.8, duration: 0.6}}
            className="mt-12 flex justify-center gap-4"
          >
            {[
              {href: "https://github.com/JuzzJoel", icon: FiGithub, label: "GitHub"},
              {
                href: "https://www.linkedin.com/in/joel-olajire-0a526a297/",
                icon: FiLinkedin,
                label: "LinkedIn",
              },
              {href: "mailto:joelolajiire@gmail.com", icon: HiOutlineMail, label: "Email"},
            ].map(({href, icon: Icon, label}) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="p-3 rounded-none border-2 border-zinc-800 bg-zinc-900/30 text-zinc-400 hover:text-yellow-400 hover:border-yellow-400/50 transition-none hover-glitch"
              >
                <Icon size={18} />
              </a>
            ))}
          </m.div>
        </m.div>
      </div>

      <m.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{delay: 1.2, duration: 0.6}}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <m.div
          animate={{y: [0, 8, 0]}}
          transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
          className="w-6 h-10 rounded-none border-2 border-zinc-800 flex items-start justify-center p-1.5"
        >
          <m.div
            animate={{y: [0, 12, 0]}}
            transition={{duration: 2, repeat: Infinity, ease: "easeInOut"}}
            className="w-1.5 h-1.5 rounded-none bg-yellow-400"
          />
        </m.div>
      </m.div>
    </section>
  );
}
